import React from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';

import {
  isAdvancedMode,
  isAdvancedVideoEditor,
  videoRouteFromPath,
  videoTabRoutes,
  viewForUrl,
  type VideoView,
} from './video-routes';
import { adRouteFromPath, adTabRoutes, type AdTab, type AdView } from './asset-routes';
import { useAppDispatch, useAppSelector } from '@/store';
import type { StoryboardPipeline } from '@/store/slices/creativeJobs';
import { fetchOwnerWallets } from '@/store/slices/wallet';
import { fetchCreditsPricing, creditCostFor } from '@/store/slices/aiCredits';
import {
  selectCreativeJobsMap,
  creativesSurfaceJobs,
  activeRunsByRoot,
  activeGenerateProgressByRoot,
  activeFormatsVersionByRoot,
} from '@/store/slices/creativeJobs';
import { Icon, Skeleton, EmptyState, Toast, Text, Callout, Button } from '../../components';
import { ftuxSignal } from '../../features/ftux/signals';
import type { FtuxTargetId } from '../../features/ftux/targets';
import { useToast } from '../../hooks/useToast';
import { useFeatureGate } from '../../hooks/useFeatureGate';
import { toBackendLang } from '@/nova/config/langCodeMap';
import {
  creativesServiceConfigured,
  generateBannerFormats,
  HERO_PLACEMENT,
  type CreativeItem,
  type CreativePlacement,
  type CreativeCarousel,
  type CreativeVersion,
} from './creative-api';
import { previewTenantSource } from './creatives-tenant-source';
import { useOrderedSource } from './hooks/useOrderedSource';
import { effectiveSubTabOf, initialSubTab, visibleSubTabsOf, type SubTab } from './sub-tab-param';
import { groupBannersByCategory } from './banner-categories';
import {
  resolveSiteUrl,
  type BannerClickMessage,
  type PreviewTarget,
  type PreviewDataSource,
  type DeviceMode,
} from './live-preview';
import { useCreatives, type GenerateInput } from './useCreatives';
import { bannersViewOf, stripOverBannersState, type BannersView } from './banners-view';
import { useBrandSiteHost } from './site-host';
import { aboveTheFoldSettledShows, aboveTheFoldShows, useAboveTheFold } from './useAboveTheFold';
import { useNewLook, useNewLookFormats, useOnRedrawn } from './useNewLook';
import { isNewLookPhaseStatus } from './new-look-api';
import { useAtfCardsRun } from './useAtfCardsRun';
import { useAtfCardRedraws } from './useAtfCardRedraws';
import { ATF_CARDS_DRAWS, redrawDaysLeft } from './above-the-fold-api';
import { firstBrandLogoUrl, LOGO_CHROME_SLOTS } from './brand-logo';
import { CreativeCard } from './components/CreativeCard';
import { CarouselCuration } from './components/CarouselCuration';
import { AboveTheFold, AboveTheFoldGlyph } from './components/AboveTheFold';
import { EditCreativeSheet } from './components/EditCreativeSheet';
import { BuyCreditsSheet } from './components/BuyCreditsSheet';
import { LivePreview } from './components/LivePreview';
import { CustomStudio } from './components/CustomStudio';
import { GameLists } from './components/GameLists';
import { MarketingAreaHeader } from '../marketing-assets/components/MarketingAreaHeader';
import { WebsitePage } from '../website/components/WebsiteShell';
import { brandLogoSource } from '../casino-settings/logos';
import { adLogoSet, adLogoUrl, gameLogos } from './ad-subjects';
import { VideoLibrary } from './components/VideoLibrary';
import { BannerKitLibrary } from './components/BannerKitLibrary';
import { Html5BannerLibrary } from './components/Html5BannerLibrary';
import { PlayableLibrary } from './components/PlayableLibrary';
import { LandingPageLibrary } from './components/LandingPageLibrary';
import { useImmersive } from '@/nova/layouts/MainLayout/immersiveControl';
import { useMediaQuery } from '@/nova/hooks/useMediaQuery';
import { NAV_DESKTOP } from '@/nova/theme/tokens';
import {
  SurfaceTabs,
  SurfaceTab,
  TabSep,
  SwitchFade,
  Section,
  SectionHead,
  SectionTitleRow,
  SectionName,
  Count,
  Grid,
} from './creatives.styles';
import { useBrandSettled } from '../website/useBrandSettled';
import { PreviewOverlay } from './components/PreviewOverlay';
import { DevicePreviewPill } from './components/DevicePreviewPill';

/** Which view the route opened: Website → Website Creatives (`banners`) or Game Lists (`games`), or
 *  Marketing → Marketing Assets (`custom`). Each is its own route (see router.tsx), which gates it. */
export type CreativesSection = 'banners' | 'games' | 'custom';

export type CreativesProps = {
  section?: CreativesSection;
  styleText?: string;
  className?: string;
};

/** Sub-tabs within Marketing Assets: Image Banners (the banner kits), HTML5 Banners, Video Ads (v1, stitched beats),
 *  Video Ads v2.0 (single clip), Mini-games, and the older image studio (Image Ads, shown only while Image Banners is
 *  off). Each video tab is its own library of the ads made with its pipeline; the ad tabs are the libraries of those
 *  entities. */
type AssetTab = 'images' | 'advanced-video' | 'video-v2' | AdTab;

/** The video tab that creates (and lists) a pipeline's ads, and back. */
const VIDEO_TAB_OF: Record<StoryboardPipeline, AssetTab> = { beats: 'advanced-video', single_clip: 'video-v2' };
const pipelineOfTab = (tab: AssetTab): StoryboardPipeline | undefined =>
  tab === 'advanced-video' ? 'beats' : tab === 'video-v2' ? 'single_clip' : undefined;
/** The ad tab (Image Banners / HTML5 Banners / Mini-games / Landing Pages) a sub-tab is, or undefined for the image and
 *  video tabs. */
const adTabOf = (tab: AssetTab): AdTab | undefined =>
  tab === 'banner-kits' || tab === 'html5-banners' || tab === 'playables' || tab === 'landing-pages' ? tab : undefined;

/** Job kinds whose failures their own composer reports (video, storyboard, and the banner-kit / mini-game entity
 *  jobs, a Stop included), so the generic banner/publish failure toast must not claim them. */
const COMPOSER_OWNED_JOB_KINDS = new Set([
  'video',
  'storyboard',
  'banner_kit',
  'playable_assets',
  'kit_layers',
  'landing_page',
]);

/** Friendly names for the logo-family slots (the live source today). English copy is the i18n
 *  key; an unmapped slot falls back to its raw id. */
const SLOT_LABELS: Record<string, string> = {
  topNavDesktop: 'Top nav (desktop)',
  topNavMobile: 'Top nav (mobile)',
  onboarding: 'Onboarding & emails',
  footer: 'Footer',
  symbol_and_name_horizontal: 'Primary logo',
  symbol_and_name_vertical: 'Stacked logo',
  name_horizontal: 'Wordmark',
  symbol_and_short_name_horizontal: 'Symbol + short name',
  name_horizontal_black: 'Wordmark (black)',
  name_horizontal_white: 'Wordmark (white)',
  symbol: 'Symbol',
  short_name: 'Short name',
};

/* Where the FTUX's banners piece lights the pool's first banner, its Edit marked inside it (inert outside an FTUX step). */
const FIRST_BANNER_TARGET: FtuxTargetId = 'banners-first-card';
const FIRST_BANNER_EDIT_TARGET: FtuxTargetId = 'banners-first-edit';

/**
 * Creatives — the brand's creative kit. Owners list the AI-generated banners running across
 * their site and change any of them by talking to Lumio (or restyling, or uploading their own),
 * always inside their chosen art style. Every AI action spends credits, shown in the header and
 * bought from the wallet. See the Creative Kit design brief.
 *
 * BUILD-AHEAD: the banner-family read + the credits ledger are backend contracts that do not
 * ship yet (brief, Contracts A + B). Until they answer, the page shows honest loading / empty
 * states rather than any placeholder artwork, and every wired call lights up the moment the
 * backend exists, exactly like the launched-brand logo-replace flow.
 */
const Creatives = ({ section = 'banners', styleText = '', className = '' }: CreativesProps): React.ReactElement => {
  const { t, i18n } = useTranslation();
  const { toast, show } = useToast();
  const dispatch = useAppDispatch();

  // Active brand — the lagged displayBrand, like the other pages; resolve its tenant + name.
  const displayBrand = useAppSelector((state) => state.casinoInstance.displayBrand);
  const tenantRaw = Number(displayBrand?.tenant_id);
  const tenantId = Number.isFinite(tenantRaw) && tenantRaw > 0 ? tenantRaw : undefined;
  const brandName = displayBrand?.casino_data?.details?.brand ?? '';
  const brandDomain = displayBrand?.casino_data?.details?.domain;
  // No brand to read for: none will resolve (the list answered with none, or failed), or the one selected has no usable
  // tenant id. Its creatives are never read, so the page shows its empty state instead of a skeleton that never ends.
  const brandSettled = useBrandSettled();
  const noBrand = !tenantId && brandSettled;
  // The brand's live-site base, for opening a banner's link destination in a new tab. Stays the
  // PUBLIC domain: it is the brand's real, shareable address, and a standing navigable link must not
  // expose the internal cluster host (a direct-to-origin URL past the public domain's edge protections
  // on prod). Only the sandboxed preview iframe below takes the internal host.
  const siteBase = brandDomain
    ? /^https?:\/\//i.test(brandDomain)
      ? brandDomain
      : `https://${brandDomain}`
    : undefined;
  // `details.logo` may be a plain URL or a per-slot logo map; the studio wants a single URL.
  const brandLogoRaw = displayBrand?.casino_data?.details?.logo;
  const brandLogoUrl = firstBrandLogoUrl(brandLogoRaw);
  // Kits and HTML5 banners sit on dark grounds: they start from the white wordmark when the brand has one.
  const adBrandLogoUrl = adLogoUrl(brandLogoSource(displayBrand ?? undefined), brandLogoUrl);
  // Every variant of the brand's logo: Image Banners place the one that fits each size (in code, never redrawn).
  const adBrandLogos = React.useMemo(() => adLogoSet(brandLogoSource(displayBrand ?? undefined)), [displayBrand]);
  // Mini-games show the brand's primary logo (and its bare symbol on the offer-wheel hub) on surfaces that take the
  // logo's own colours.
  const adGameLogos = React.useMemo(
    () => gameLogos(brandLogoSource(displayBrand ?? undefined), brandLogoUrl),
    [displayBrand, brandLogoUrl],
  );
  // The host embedded in the SANDBOXED preview iframe (via the sanctioned dsi bridge): the internal
  // host casino-creation deployed under (always reachable) preferred over the public domain (usually
  // not connected off prod), mirroring onboarding's "Preview Website". Empty while it resolves / when
  // the brand has no reachable host — the page then relies on the on-page banner rail alone. Every
  // backend call keeps `brandDomain` (the services' canonical site key). See ./site-host.
  const { previewDomain, resolving: siteHostResolving } = useBrandSiteHost(tenantId, brandDomain);
  // Preview site keys on the backend/country language codes (arg -> ar, bra -> br).
  const siteUrl = resolveSiteUrl(previewDomain, undefined, toBackendLang((i18n.language || 'en').split('-')[0]));

  const creatives = useCreatives(tenantId, brandName, brandDomain);
  // What the home page shows first (Website Creatives only). It is read with the preview host, as Game Lists reads
  // the site's pages: the service resolves the site from its own page, and off production the public domain is often
  // not connected.
  const siteHost = React.useMemo(() => {
    try {
      return siteUrl ? new URL(siteUrl).host : '';
    } catch {
      return '';
    }
  }, [siteUrl]);
  // Above the fold is read only while its tab may show (`creatives-above-the-fold`): off, nothing is read, so the tab,
  // its redraws and a ?view=above-the-fold link all fall back to the pool.
  const aboveTheFoldOn = useFeatureGate().isEnabled('creatives-above-the-fold');
  const aboveTheFold = useAboveTheFold(
    section === 'banners' && aboveTheFoldOn ? tenantId : undefined,
    siteHost,
    siteHostResolving,
  );
  // The New look a website regeneration (Art Style) draws per banner, shown read-only on its cards and in its sheet.
  const newLook = useNewLook(section === 'banners' ? tenantId : undefined);
  // A regeneration in place redraws the live banners: read them again as each one is redrawn.
  useOnRedrawn(newLook, creatives.reload);

  // In-flight runs, keyed by the banner they target, so a card shows a busy overlay while it
  // regenerates or publishes (instead of a floating tray). `root_banner_id` comes from the job.
  const jobsById = useAppSelector(selectCreativeJobsMap);
  // The jobs this page shows: every run minus the Art Style sample draws (tracked on the Art Style page).
  const surfaceJobs = React.useMemo(() => creativesSurfaceJobs(jobsById), [jobsById]);
  const activeByRoot = React.useMemo(() => activeRunsByRoot(surfaceJobs), [surfaceJobs]);
  // Real per-format progress of each banner's in-flight multi-slot draw (completed/total, streamed +
  // persisted by the backend), driving the determinate progress bar on the busy dot-field.
  const genProgressByRoot = React.useMemo(() => activeGenerateProgressByRoot(surfaceJobs), [surfaceJobs]);
  // The version each in-flight formats run is completing (root → version id), so the editor anchors the
  // in-thumb generating overlay to the exact version — reliably, even after a page refresh.
  const formatsVersionByRoot = React.useMemo(() => activeFormatsVersionByRoot(surfaceJobs), [surfaceJobs]);
  const busyLabelFor = React.useCallback(
    (bannerId: string): string | undefined => {
      const kind = activeByRoot[bannerId];
      if (!kind) return undefined;
      return kind === 'publish' ? t('Publishing…') : t('Updating…');
    },
    [activeByRoot, t],
  );

  // Surface a run FAILURE once, as a toast (the tray that used to show this was removed). Only for
  // runs seen active this session (so historical failures don't re-toast on load) and this brand.
  const seenActiveRef = React.useRef<Set<string>>(new Set());
  const toastedFailRef = React.useRef<Set<string>>(new Set());
  React.useEffect(() => {
    const jobs = surfaceJobs;
    for (const j of jobs) {
      if (j.status === 'queued' || j.status === 'running') seenActiveRef.current.add(j.id);
    }
    for (const j of jobs) {
      const mine = String(j.tenant_id) === String(tenantId);
      // Video, storyboard, banner-kit and mini-game failures are owned by their composer's own status copy, so the
      // generic banner/publish toast must not claim such a run "couldn't update the banner".
      if (
        j.status === 'failed' &&
        !COMPOSER_OWNED_JOB_KINDS.has(j.kind ?? '') &&
        mine &&
        seenActiveRef.current.has(j.id) &&
        !toastedFailRef.current.has(j.id)
      ) {
        toastedFailRef.current.add(j.id);
        show(
          j.kind === 'publish'
            ? t("Couldn't publish to your live site. Please try again.")
            : t("Couldn't update the banner. Please try again."),
        );
      }
    }
  }, [surfaceJobs, tenantId, show, t]);

  // Deep-link view: the Marketing Assets `/videos[/new|/:id]` (Video Ads) and `/videos-v2[/new|/:id]` (Video Ads
  // v2.0) routes name a video tab's library / editor. Read at mount and re-applied by the effect below when the
  // pathname changes under a live instance (the Marketing Assets root and all its video routes are ONE page).
  // Null on the plain Marketing Assets route, where the
  // sub-tabs own the view. (Which SECTION shows is the route's call: see `section`.)
  const { pathname, search } = useLocation();
  const navigate = useNavigate();
  const urlVideoRoute = React.useMemo(() => videoRouteFromPath(pathname), [pathname]);
  const urlVideoView = urlVideoRoute?.view ?? null;
  // The Image Banners `/banners[/new|/:id]`, HTML5 Banners `/html5-banners[/new|/:id]` and Mini-games
  // `/mini-games[/new|/:id]` deep links, same model.
  const urlAdRoute = React.useMemo(() => adRouteFromPath(pathname), [pathname]);

  // The sub-tab the owner (or a deep link) chose within Marketing Assets; null = the default, the first available
  // tab (see `assetTabs` below: each tab appears only once its gates resolve).
  const [assetTab, setAssetTab] = React.useState<AssetTab | null>(() =>
    urlVideoRoute ? VIDEO_TAB_OF[urlVideoRoute.pipeline] : urlAdRoute?.tab ?? null,
  );
  // The sub-tab within Website Creatives: the pool ("all Promotions", where banners are authored) or one
  // of the curated carousels (Homepage / Casino / Sports, where pool banners are picked + ordered). A `?view=`
  // deep link (the Website hub's Creatives card) picks the first one; it is read once and left in the URL.
  const [subTab, setSubTab] = React.useState<SubTab>(() => (section === 'banners' ? initialSubTab(search) : 'pool'));
  // The curated Carousels are generally available (the `creatives-carousels` flag ships enabled, so
  // `carouselsEnabled` is true for everyone); the gate is kept as a registry-driven rollback switch. When it
  // is off we drop the carousel tabs AND fall back to the banner pool (derive, not force: the state is left
  // intact so a live flip just re-shows it). The other sections render via their own routes: Game Lists is
  // generally available (its `creatives-games` route gate is a rollback switch), and so is Marketing
  // (`creatives-assets`).
  const gate = useFeatureGate();
  const carouselsEnabled = gate.isEnabled('creatives-carousels');
  // Video generation has its own gate (generally available; `creatives-video` is a rollback switch, and its hub
  // card still waits for the `video` price row). Its credit cost drives the composer's cost chip.
  const videoEnabled = gate.isEnabled('creatives-video');
  // Storyboard ads reuse the `creatives-video` gate; the section renders only once its per-beat price
  // resolves (a data-driven on-switch, with the server 402 staying authoritative).
  const beatCost = useAppSelector((state) => creditCostFor(state.aiCredits?.pricing, 'video_beat'));
  // Whether the credit price table has settled, so a /videos deep link can hold a skeleton until the Video
  // Ads sub-tab is derivable (it needs `beatCost`) instead of flashing the Image studio fallback.
  const pricingLoaded = useAppSelector((state) => state.aiCredits?.pricingLoaded ?? false);
  // Image Banners (banner kits), HTML5 Banners and Mini-games each have their own gate (generally available, kept as
  // rollback switches); each tab appears once its gate is on and the price table has settled (their composers price
  // every render from it).
  const bannerKitsEnabled = gate.isEnabled('creatives-banner-kits');
  const html5BannersEnabled = gate.isEnabled('creatives-html5-banners');
  const playablesEnabled = gate.isEnabled('creatives-playables');
  const landingPagesEnabled = gate.isEnabled('creatives-landing-pages');
  // Marketing Assets sub-tabs, in order: Image Banners, HTML5 Banners, Video Ads, Mini-games. Video Ads (and Video
  // Ads v2.0) appear once the video gate is on AND the per-beat price resolves (v2 prices a clip in beat units); the
  // ad tabs once their gates are on and pricing has loaded. Image Ads (the older image studio) is hidden whenever
  // Image Banners is on (its gate alone decides, so Image Ads never flashes while the prices load); with Image
  // Banners off it leads: it is there from the first render, so the default tab never moves when the priced tabs
  // join (which would unmount the image studio mid-use).
  const assetTabs: AssetTab[] = React.useMemo(() => {
    const tabs: AssetTab[] = [];
    if (!bannerKitsEnabled) tabs.push('images');
    if (bannerKitsEnabled && pricingLoaded) tabs.push('banner-kits');
    if (html5BannersEnabled && pricingLoaded) tabs.push('html5-banners');
    if (videoEnabled && beatCost !== undefined) tabs.push('advanced-video');
    if (playablesEnabled && pricingLoaded) tabs.push('playables');
    if (landingPagesEnabled && pricingLoaded) tabs.push('landing-pages');
    return tabs;
  }, [
    videoEnabled,
    beatCost,
    bannerKitsEnabled,
    html5BannersEnabled,
    playablesEnabled,
    landingPagesEnabled,
    pricingLoaded,
  ]);
  // Video Ads v2.0 has no tab for now, but stays reachable by its own /videos-v2 links (a bookmarked or freshly
  // minted single-clip ad), so its ads never become unreachable while the tab is hidden.
  const reachableTabs: AssetTab[] = React.useMemo(
    () => (assetTabs.includes('advanced-video') ? [...assetTabs, 'video-v2'] : assetTabs),
    [assetTabs],
  );
  // The sub-tab on screen: the chosen one while it is available, else the first available (undefined while none is
  // yet, i.e. Image Banners is on and the prices are still loading: the view holds a skeleton).
  const effectiveAssetTab: AssetTab | undefined =
    assetTab !== null && reachableTabs.includes(assetTab) ? assetTab : assetTabs[0];
  // A Video Ads link whose tab cannot open: the prices have landed without the per-beat one (a failed read, or a
  // cluster without the row), so the tab never appears. Said plainly instead of falling back to another studio under
  // the Video Ads header.
  const videoUnpriced = !!urlVideoRoute && videoEnabled && pricingLoaded && beatCost === undefined;
  // The pipeline of the video tab on screen (undefined on the other tabs).
  const activeVideoPipeline = effectiveAssetTab ? pipelineOfTab(effectiveAssetTab) : undefined;
  // The ad tab on screen (undefined on the image and video tabs).
  const activeAdTab = effectiveAssetTab ? adTabOf(effectiveAssetTab) : undefined;
  // The Marketing area the header names: the one the URL names (each area is its own route, reached from the hub,
  // so it is known before the prices that gate its tab land), else the last one shown. Leaving for the hub, this
  // instance re-renders at the hub's URL while it slides out, and so keeps its header instead of losing it mid-slide.
  const marketingArea: AssetTab | undefined = urlVideoRoute
    ? VIDEO_TAB_OF[urlVideoRoute.pipeline]
    : urlAdRoute?.tab ?? effectiveAssetTab;
  // A tab's display name. Literal t() per id so the extractor harvests each.
  const assetTabLabel = React.useCallback(
    (id: AssetTab): string => {
      switch (id) {
        case 'advanced-video':
          return t('Video Ads');
        case 'video-v2':
          return t('Video Ads v2.0');
        case 'banner-kits':
          return t('Image Banners');
        case 'html5-banners':
          return t('HTML5 Banners');
        case 'playables':
          return t('Mini-games');
        case 'landing-pages':
          return t('Landing Pages');
        default:
          return t('Image Ads');
      }
    },
    [t],
  );
  // Full-screen (immersive) mode for the Advanced Video editor: on that sub-tab the shared layout slides
  // the side menu out and the canvas owns the screen; the layout's back button exits back to Images.
  const { setImmersive } = useImmersive();
  // Desktop only: the side menu (and its slide-out) exist only at NAV_DESKTOP; on mobile the sub-tabs stay
  // and the editor keeps the normal layout, so a mobile user is never stranded behind a hidden menu.
  const isDesktop = useMediaQuery(NAV_DESKTOP);
  // The Video Ads tab shows the LIBRARY grid by default; a /videos/new or /videos/:id URL (opening or creating
  // an ad) enters the immersive full-screen editor. The URL is the source of truth: VideoLibrary navigates to
  // enter/leave the editor, and this state is derived from the pathname (below) so back/forward just work.
  const [videoView, setVideoView] = React.useState<VideoView>(() => urlVideoView ?? { kind: 'library' });
  React.useEffect(() => {
    // A /videos* or /videos-v2* pathname (deep link, or a pathname change under this live instance) forces the
    // sub-tab to the URL's video tab and the view to it. The plain Marketing Assets route leaves the sub-tabs in
    // charge but closes any open editor or new-ad flow (see viewForUrl): Back from an ad lands here on the SAME
    // page instance, so nothing remounts to close it.
    if (urlVideoRoute) setAssetTab(VIDEO_TAB_OF[urlVideoRoute.pipeline]);
    setVideoView((v) => viewForUrl(urlVideoRoute, v));
  }, [urlVideoRoute]);
  // The Banner Kits / Mini-games view, derived from the URL the same way: a `/banners*` or `/mini-games*` pathname
  // forces its tab and names the view (library, new, or an entity by id); any other Marketing Assets route closes
  // an open composer or create flow. The URL is the source of truth; the libraries navigate to open / close.
  const [adView, setAdView] = React.useState<AdView>(() => urlAdRoute?.view ?? { kind: 'library' });
  React.useEffect(() => {
    if (urlAdRoute) setAssetTab(urlAdRoute.tab);
    setAdView((v) => viewForUrl(urlAdRoute, v));
  }, [urlAdRoute]);
  // Immersive (full-screen canvas) is the ADVANCED editor only: `?mode=advanced` on a /videos/:id (or /new), on the
  // Video Ads (v1) tab only (Video Ads v2.0 has no advanced canvas). The default editor is the easy MODAL, which is
  // a normal modal over the library and never immersive.
  const advancedImmersed =
    isDesktop &&
    section === 'custom' &&
    effectiveAssetTab === 'advanced-video' &&
    videoView.kind !== 'library' &&
    isAdvancedMode(search);
  // useLayoutEffect (not useEffect) so `data-immersive` is stamped on `.pg-layout` BEFORE first paint —
  // the layout-collapse global styles then apply on the same frame the editor mounts, with no one-frame
  // flash of the gutters / content card. The back button PUSHes to the library URL (mirrors the blog editor's
  // BackHeader), so browser history stays coherent.
  React.useLayoutEffect(() => {
    if (!advancedImmersed) return undefined;
    // The immersive back button returns to the EASY editor for the same ad (drop ?mode=advanced, keep the
    // /videos/:id path), not all the way out to the library. Reads the live pathname so it's never stale.
    setImmersive(true, () => navigate(window.location.pathname));
    return () => {
      // Only drop immersive when actually LEAVING the editor. The page transition does NOT freeze the URL for
      // the OUTGOING page instance, so on entry the exiting library instance re-renders at the new /videos/:id,
      // turns immersive on too, then unmounts ~after the slide and would clobber the incoming editor's state.
      // Guard on the LIVE url: if it is still an editor route, another instance owns immersive, so leave it on.
      if (!isAdvancedVideoEditor(window.location.pathname, window.location.search)) setImmersive(false);
    };
  }, [advancedImmersed, setImmersive, navigate]);
  // Brand switch while editing: the composer autosaves by (tenantId, entity id), so a switch mid-edit would
  // write the old brand's draft into the new tenant. Bounce to the library on a real tenant change (never on
  // the initial resolve, which would strand a deep link).
  const openedForTenant = React.useRef<number | undefined>(tenantId);
  React.useEffect(() => {
    if (tenantId === undefined) return; // brand still resolving on boot: not a switch
    const previous = openedForTenant.current;
    openedForTenant.current = tenantId;
    if (previous === undefined || previous === tenantId) return;
    if (activeAdTab && adView.kind !== 'library') {
      // A banner kit / mini-game composer autosaves the same way: back to its tab's library.
      navigate(adTabRoutes(activeAdTab).library(), { replace: true });
    } else if (videoView.kind !== 'library') {
      // Back to the library of the video tab the editor was open in.
      navigate(videoTabRoutes(activeVideoPipeline ?? 'beats').library(), { replace: true });
    }
  }, [tenantId, videoView.kind, activeVideoPipeline, adView.kind, activeAdTab, navigate]);
  // Above the fold leads the sub-tabs while the home page may have something there; a sub-tab that does not show
  // (an empty above the fold, a gated carousel) falls back to the pool, deep link or not.
  // With its flag off the tab never shows, not even while the site host resolves (the hook reports loading then).
  const visibleSubTabs = visibleSubTabsOf(
    aboveTheFoldOn && aboveTheFoldShows(aboveTheFold.status, aboveTheFold.atf),
    carouselsEnabled,
  );
  const effectiveSubTab: SubTab = effectiveSubTabOf(subTab, visibleSubTabs);
  // The carousel the sub-tabs are curating (undefined on the pool tab, which has no override). Its ordered
  // override (load, debounced draft autosave, publish, revert, and the revert-availability flag) is owned
  // by one hook call, RE-KEYED on the brand's casino instance per the unified tenant-source contract, not
  // the tenant id. The hook re-keys itself on the active carousel, so switching sub-tabs re-reads its state.
  const activeCarousel: CreativeCarousel | undefined =
    effectiveSubTab === 'pool' || effectiveSubTab === 'above-the-fold' ? undefined : effectiveSubTab;
  const carouselSource = useOrderedSource({
    kind: 'carousel',
    sourceId: activeCarousel,
    casinoInstanceId: displayBrand?.id,
  });
  // Alias the two callables so the handler deps below are plain identifiers: exhaustive-deps treats a
  // METHOD CALL (carouselSource.publish(...)) as a dep on the whole object, not the member, whereas a
  // plain read (carouselSource.order) is matched by path — so member reads stay inline, calls get aliased.
  const { publish: carouselPublish, revert: carouselRevert } = carouselSource;
  // What the in-website preview is focused on (a carousel's page + a banner to highlight), or null to
  // just show the site. Set by "preview on site" affordances; drives the embedded LivePreview.
  const [previewTarget, setPreviewTarget] = React.useState<PreviewTarget | null>(null);
  // Desktop preview device width (desktop / tablet / mobile), switched from the header pill.
  const [previewDevice, setPreviewDevice] = React.useState<DeviceMode>('desktop');
  const [editing, setEditing] = React.useState<{
    placement: string;
    label: string;
    bannerId?: string;
    baseImageUrl?: string;
  } | null>(null);
  // Drives an INSTANT (optimistic) preview update on publish: carries the just-published banner + its art
  // so <LivePreview> swaps it into the framed site immediately, without waiting ~5s for the backend. `seq`
  // makes each publish a distinct trigger (republishing the same banner still fires).
  const [optimisticPublish, setOptimisticPublish] = React.useState<{
    bannerId: string;
    formats?: Record<string, string>;
    imageUrl?: string;
    seq: number;
  } | null>(null);
  const [buyOpen, setBuyOpen] = React.useState(false);
  // Credit amount to preselect in the Buy sheet: the shortfall a 402 reported, or undefined when the
  // owner opens Buy from an affordance (then the sheet defaults to the money-anchored suggestions).
  const [buySuggested, setBuySuggested] = React.useState<number | undefined>(undefined);
  const openBuy = React.useCallback((suggested?: number) => {
    setBuySuggested(suggested && suggested > 0 ? suggested : undefined);
    setBuyOpen(true);
  }, []);
  // Regenerating the brand's Casino and Sports cards (Above the fold): charged the per-image draw price for each of
  // its images; the run is followed through the jobs feed and, once it lands, the cards are read again.
  const drawCost = useAppSelector((state) => creditCostFor(state.aiCredits?.pricing, 'generate'));
  const cardsRun = useAtfCardsRun({
    tenantId: section === 'banners' ? tenantId : undefined,
    domain: siteHost,
    recordId: aboveTheFold.atf?.cards?.recordId,
    onDone: aboveTheFold.reload,
    onError: show,
    onNeedCredits: openBuy,
  });
  // Redrawing one card image (charged the per-image draw price): its art is read only while the Above the fold tab
  // shows the brand's own cards, and an applied candidate reads the cards again.
  const cardRedraws = useAtfCardRedraws({
    tenantId: section === 'banners' ? tenantId : undefined,
    enabled:
      section === 'banners' && effectiveSubTab === 'above-the-fold' && Boolean(aboveTheFold.atf?.cards?.editable),
    cardsRunning: cardsRun.run !== null,
    liveImages: aboveTheFold.atf?.cards?.images,
    onApplied: aboveTheFold.reload,
    onMessage: show,
    onNeedCredits: openBuy,
  });
  // Per-action credit cost table (spark-owned) — drives the cost chips + the pre-flight affordability
  // gate. The authoritative charge is still spark's server 402; this only avoids a wasted round-trip.
  React.useEffect(() => {
    void dispatch(fetchCreditsPricing());
  }, [dispatch]);
  // The image studio hides Marketing Assets' sub-tabs while its editor is open (so its BackHeader tops the view).
  const [customImmersed, setCustomImmersed] = React.useState(false);

  // Placement labels: a fixed vocabulary for the known slots (translated), else a humanised id.
  const placementLabel = React.useCallback(
    (placement: CreativePlacement): string => {
      if (placement.label) return placement.label; // backend-provided is a data value
      const id = placement.placement;
      if (id === HERO_PLACEMENT) return t('Hero banners');
      if (id.includes('top-navigation')) return t('Top navigation');
      if (id.includes('side-panel')) return t('Side panel');
      if (id.includes('footer')) return t('Footer');
      return id
        .split(/[.\-_]/)
        .filter(Boolean)
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
    },
    [t],
  );

  // One site category token → its display label (translated for the known verticals, humanised
  // otherwise). Literal-keyed t() so the extractor harvests each.
  const categoryTokenLabel = React.useCallback(
    (token: string): string => {
      switch (token) {
        case 'CASINO':
          return t('Casino');
        case 'SPORT':
        case 'SPORTS':
          return t('Sport');
        default:
          return token
            .split(/[_-]/)
            .filter(Boolean)
            .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
            .join(' ');
      }
    },
    [t],
  );

  // A category group's section label: its tokens joined ("Casino", "Casino & Sport"), or "Other" for
  // banners with no category tag.
  const categoryLabel = React.useCallback(
    (tokens: string[]): string => (tokens.length === 0 ? t('Other') : tokens.map(categoryTokenLabel).join(' & ')),
    [categoryTokenLabel, t],
  );

  // A sub-tab's display name. Literal t() per id so the extractor harvests each.
  const subTabLabel = React.useCallback(
    (id: SubTab): string => {
      switch (id) {
        case 'above-the-fold':
          return t('Above the fold');
        case 'homepage':
          return t('Homepage');
        case 'casino':
          return t('Casino');
        case 'sports':
          return t('Sports');
        default:
          return t('All Promotions & Creatives');
      }
    },
    [t],
  );

  const openEdit = React.useCallback((placement: string, label: string): void => {
    setEditing({ placement, label });
  }, []);

  // Edit ONE specific banner (from a card click or a click inside the live preview): the sheet
  // targets that banner so the run edits it rather than adding a new creative to the placement.
  const markSeen = creatives.markSeen;
  const openEditBanner = React.useCallback(
    (placement: string, item: CreativeItem, label: string): void => {
      // Opening the editor "sees" the family — clear its new-version cue.
      if (item.has_new) markSeen(item.banner_id);
      // Title the editor by what the banner actually says, not the generic placement name.
      setEditing({
        placement,
        label: item.headline || item.internal_name || label,
        bannerId: item.banner_id,
        baseImageUrl: item.image_url,
      });
      ftuxSignal('banner-editor-opened');
    },
    [markSeen],
  );

  // A banner clicked inside the live embedded site (via the creative-bridge). Resolve it to a
  // creative in the family — by its id first (same data source, so ids match), then by image URL,
  // then by ordinal. An unmatched click is ignored (better than editing the wrong banner).
  const placementsRef = React.useRef<CreativePlacement[]>([]);
  const handleBannerClick = React.useCallback(
    (message: BannerClickMessage): void => {
      const groups = placementsRef.current;
      const flat = groups.flatMap((g) => g.items.map((item) => ({ placement: g.placement, item })));
      if (flat.length === 0) return;
      let hit = message.bannerId ? flat.find((x) => x.item.banner_id === message.bannerId) : undefined;
      if (!hit && message.imageUrl) hit = flat.find((x) => x.item.image_url === message.imageUrl);
      if (!hit && typeof message.index === 'number') hit = flat[message.index];
      if (!hit) return; // couldn't identify the banner — do nothing rather than edit the wrong one
      const target = hit; // const so it narrows inside the closure below
      const label = placementLabel(
        groups.find((g) => g.placement === target.placement) ?? { placement: target.placement, items: [] },
      );
      openEditBanner(target.placement, target.item, label);
    },
    [openEditBanner, placementLabel],
  );

  // Open the big preview modal focused on a carousel's page, highlighting a banner if given.
  const handlePreviewOnSite = React.useCallback((target: PreviewTarget): void => {
    setPreviewTarget(target);
  }, []);

  // Fetch the site-faithful preview payload for a carousel, in the owner's CURRENT (possibly unsaved)
  // draft order, so the embedded site renders the unpublished order. Best-effort: null hides the override.
  const getPreviewSource = React.useCallback(
    async (carousel: string): Promise<PreviewDataSource | null> => {
      // The hook holds ONE carousel's order at a time (the active sub-tab), so only that carousel's draft
      // order is known here; a stale request for any other carousel gets no override and the framed site
      // just shows its published order. `previewTenantSource` already swallows errors to null.
      if (!displayBrand?.id || carousel !== activeCarousel) return null;
      const res = await previewTenantSource(displayBrand.id, {
        kind: 'carousel',
        source_id: carousel,
        ids: carouselSource.order,
      });
      return res ? { dsId: res.ds_id, source: res.source } : null;
    },
    [displayBrand?.id, activeCarousel, carouselSource.order],
  );

  const handleApply = React.useCallback(
    async (placement: string): Promise<void> => {
      const ok = await creatives.apply(placement);
      if (ok) {
        show(t('Your creative is now live.'));
        setEditing(null);
      }
    },
    [creatives, show, t],
  );

  const handlePlaceOwn = React.useCallback(
    async (placement: string, dataUri: string): Promise<void> => {
      const ok = await creatives.placeOwn(placement, dataUri);
      if (ok) {
        show(t('Your creative is now live.'));
        setEditing(null);
      }
    },
    [creatives, show, t],
  );

  // Generation through the service is ASYNC: enqueuing charges credits and returns immediately,
  // and the run is tracked in the persistent tray (it survives navigating away). So on a
  // successful enqueue we close the sheet, toast that it started, and refetch the balance for the
  // header chip. The build-ahead pipeline (service unconfigured) instead resolves into a preview
  // the sheet keeps open for Apply, so we leave it open there.
  const handleGenerate = React.useCallback(
    async (placement: string, input: GenerateInput): Promise<void> => {
      const result = await creatives.generate(placement, input);
      if (result.ok && creativesServiceConfigured()) {
        void dispatch(fetchOwnerWallets());
        // Regenerating an existing banner: the composer closed on submit; the editor's version strip
        // now shows the live "generating" tile that swaps for the result. A fresh placement generate
        // has no strip to morph, so it toasts and closes as before.
        if (input.bannerId) return;
        show(t("Generating your creative. We'll add it here when it's ready."));
        setEditing(null);
        return;
      }
      // Insufficient credits (a 402): open Buy-credits rather than a dead-end "try again" toast.
      if (result.needed != null) {
        openBuy(result.needed);
        return;
      }
      // The composer closed on submit, so a failed START must be surfaced here (a failed RUN is
      // toasted from the jobs watcher above). A refusal the owner can act on says why.
      if (!result.ok && input.bannerId && creativesServiceConfigured()) {
        if (result.refusal === 'banner_definition_unavailable') {
          show(t('This banner has no definition to draw from any more. Start from the current banner instead.'));
        } else if (result.refusal === 'art_style_not_ready') {
          show(t("That art style isn't ready to draw with yet. Pick another, or finish it on the Art Style tab."));
        } else {
          show(t("That couldn't be started. Please try again."));
        }
      }
    },
    [creatives, show, t, dispatch, openBuy],
  );

  // Publish a version to the brand's live site (async job; the tray tracks it). A 402 opens
  // Buy-credits. The sheet stays open so its confirm resets cleanly.
  const handlePublish = React.useCallback(
    async (rootBannerId: string, versionId: string, version?: CreativeVersion): Promise<void> => {
      const result = await creatives.publish(rootBannerId, versionId);
      if (result.ok) {
        show(t('Publishing to your live site. It will update shortly.'));
        // Optimistic: we already have the published version's art client-side, so update the live preview
        // instantly instead of waiting ~5s for the backend. LivePreview swaps it in + reconciles later.
        if (version && (version.image_url || (version.formats && Object.keys(version.formats).length))) {
          setOptimisticPublish((prev) => ({
            bannerId: rootBannerId,
            formats: version.formats,
            imageUrl: version.image_url,
            seq: (prev?.seq ?? 0) + 1,
          }));
        }
        void dispatch(fetchOwnerWallets());
        return;
      }
      if (result.needed != null) {
        openBuy(result.needed);
        return;
      }
      show(t("That couldn't be published. Please try again."));
    },
    [creatives, show, t, dispatch, openBuy],
  );

  // Switch the sub-tab (pool / a carousel) within Website Creatives.
  const onSubTabClick = React.useCallback((event: React.MouseEvent<HTMLElement>): void => {
    const id = event.currentTarget.dataset.id as SubTab | undefined;
    if (id) setSubTab(id);
    // Centre the tapped tab within the horizontally-scrollable strip (mobile), so a partially-hidden
    // tab pulls itself into view instead of switching content while staying off-screen. A no-op on
    // desktop where the row isn't scrollable and the tab is already visible.
    event.currentTarget.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
  }, []);

  // Publish the active carousel's shown order to the site, then toast. Pass the explicit on-screen order
  // (carouselSource.order) so a WYSIWYG publish is deterministic even mid-debounce (an empty carousel is a
  // valid clear). A refusal surfaces the server's own message (the owner can act on it); anything else is a
  // generic retry. The hook cancels the pending autosave and applies the published order on success.
  const handleCarouselPublish = React.useCallback(
    async (label: string): Promise<void> => {
      const r = await carouselPublish(carouselSource.order);
      show(
        r.ok
          ? t('{{label}} carousel published to your site.', { label })
          : r.reason === 'refused' && r.message
            ? r.message
            : t("That couldn't be published. Please try again."),
      );
    },
    [carouselPublish, carouselSource.order, show, t],
  );

  // Revert the active carousel's last publish (restore the previously published order), then toast. Same
  // refusal/failure rules as publish; the hook drops any pending autosave and applies the restored order.
  const handleCarouselRevert = React.useCallback(
    async (label: string): Promise<void> => {
      const r = await carouselRevert();
      show(
        r.ok
          ? t('{{label}} carousel reverted to the previous published order.', { label })
          : r.reason === 'refused' && r.message
            ? r.message
            : t("That couldn't be reverted. Please try again."),
      );
    },
    [carouselRevert, show, t],
  );

  // Generate a main-only version's remaining formats (charged) — the standalone action, and the step
  // publishing needs first. The async 'formats' job streams progress via the same SSE the bar reads.
  const handleGenerateFormats = React.useCallback(
    async (rootBannerId: string, versionId: string): Promise<void> => {
      if (!tenantId) return;
      // Pass the brand domain so the service resolves the tenant's real site_id for the format-slot read.
      const result = await generateBannerFormats(tenantId, rootBannerId, versionId, brandDomain);
      if (result.ok) {
        if (!result.complete) show(t('Generating all formats. This can take a couple of minutes.'));
        void dispatch(fetchOwnerWallets());
        return;
      }
      if (result.needed != null) {
        openBuy(result.needed);
        return;
      }
      show(t("Those formats couldn't be generated. Please try again."));
    },
    [tenantId, brandDomain, show, t, dispatch, openBuy],
  );

  // Archive a version; it moves into "Archived" optimistically (instant), then the server confirms. The
  // initial / a published version can't be archived (the editor disables the action, and the backend rejects it).
  const handleDeleteVersion = React.useCallback(
    async (versionId: string): Promise<void> => {
      const ok = await creatives.deleteVersion(versionId);
      show(ok ? t('Version archived.') : t("That version couldn't be archived."));
    },
    [creatives, show, t],
  );

  // Recover an archived version back into the family.
  const handleRestoreVersion = React.useCallback(
    async (versionId: string): Promise<void> => {
      const ok = await creatives.restoreVersion(versionId);
      show(ok ? t('Version recovered.') : t("That version couldn't be recovered."));
    },
    [creatives, show, t],
  );

  const placements = creatives.family?.placements ?? [];
  // Keep the live-preview click handler reading the current family without re-binding its listener.
  placementsRef.current = placements;
  // The live source today is the brand's logo family (owner-accessible + a working replace
  // pipeline). Banners are build-ahead: when Contract B ships, `source` becomes 'banners' and
  // the AI generate/restyle modes turn on.
  const isLogos = creatives.family?.source === 'logos';
  const slotLabel = (item: { banner_id: string; internal_name?: string }): string =>
    t(SLOT_LABELS[item.banner_id] ?? item.internal_name ?? item.banner_id);

  /** The banners section's own state while the banners are not listed (see `bannersViewOf`); null once they are. */
  const renderBannersState = (bannersView: BannersView): React.ReactElement | null => {
    if (bannersView === 'skeleton') {
      return (
        <Section>
          <Grid>
            {[0, 1, 2, 3].map((i) => (
              <Skeleton key={i} block height={196} radius={14} />
            ))}
          </Grid>
        </Section>
      );
    }
    if (bannersView === 'error') {
      // The read failed: say so and offer a retry. "No creatives" is only for a read that answered with none.
      return (
        <Section>
          <Callout
            tone="danger"
            title={t("We couldn't load this page")}
            text={t('Please try again.')}
            action={<Button variant="outline" icon="refresh" label={t('Try again')} onClick={creatives.reload} />}
          />
        </Section>
      );
    }
    if (bannersView === 'empty') {
      return (
        <EmptyState
          text={t(
            "We couldn't find any creatives for this brand yet. Once your site's banners are generated, they'll appear here to edit.",
          )}
        />
      );
    }
    return null;
  };

  // Banners: every creative on the site, listed and individually editable.
  const renderBanners = (): React.ReactElement => {
    const bannersView = bannersViewOf({ ...creatives, noBrand });
    // The banners' own state while they are not listed (still loading, the read failed, none yet).
    const bannersState = renderBannersState(bannersView);
    // Above the fold is read on its own: once it has something to show (or its read failed, so its retry is reachable),
    // the sub-tabs stay over the banners' state (it then fills the pool and the carousels), so its tab is reachable for
    // a brand whose banners are not listed.
    const aboveTheFoldReachable =
      tenantId !== undefined && aboveTheFoldSettledShows(aboveTheFold.status, aboveTheFold.atf);
    if (bannersState && (isLogos || !stripOverBannersState(bannersView, aboveTheFoldReachable))) return bannersState;
    // Logos keep their per-placement sections (Top navigation, Footer, …) since those are genuinely
    // distinct slots. Real banners are listed all together in one grid — no category split.
    if (isLogos) {
      return (
        <>
          {placements.map((placement) => {
            const label = placementLabel(placement);
            return (
              <Section key={placement.placement}>
                <SectionHead>
                  <SectionTitleRow>
                    <SectionName text={label} />
                    <Count>{placement.items.length}</Count>
                  </SectionTitleRow>
                </SectionHead>
                <Grid>
                  {placement.items.map((item, index) => (
                    <CreativeCard
                      key={item.banner_id}
                      enterIndex={index}
                      item={item}
                      placementLabel={slotLabel(item)}
                      linkBase={siteBase}
                      busy={creatives.pending?.placement === item.banner_id}
                      onEdit={() => openEdit(item.banner_id, slotLabel(item))}
                    />
                  ))}
                </Grid>
              </Section>
            );
          })}
        </>
      );
    }

    // The banners live in ONE global pool ("all Promotions"); the sub-tabs are that pool plus the three
    // curated carousels (Homepage / Casino / Sports), which pick + order pool banners.
    const pool = placements[0];
    const poolItems = pool ? pool.items : [];
    // The pool subgroups its banners by category (Casino / Casino & Sport).
    const groups = pool ? groupBannersByCategory([pool]) : [];
    return (
      <>
        {/* The curated Carousels (Homepage / Casino / Sports) are generally available now
            (creatives-carousels ships enabled); the gate stays as a rollback switch, and when it is off
            only the pool tab shows and the strip collapses to the library. */}
        <SurfaceTabs role="tablist" aria-label={t('Website Creatives sections')}>
          {visibleSubTabs.map((id, idx) => (
            <React.Fragment key={id}>
              {/* Divide above the fold from the pool ("All Promotions & Creatives"), and the pool from the curated
                  carousels. */}
              {visibleSubTabs[idx - 1] === 'above-the-fold' || visibleSubTabs[idx - 1] === 'pool' ? (
                <TabSep aria-hidden="true" />
              ) : null}
              <SurfaceTab
                type="button"
                role="tab"
                data-id={id}
                $active={effectiveSubTab === id}
                aria-selected={effectiveSubTab === id}
                onClick={onSubTabClick}
              >
                {/* The pool tab leads with a grid glyph — it's the library everything is picked from. */}
                {id === 'pool' ? <Icon name="grid" size={15} /> : null}
                {/* Above the fold leads with its own glyph: a page cut off at the fold. */}
                {id === 'above-the-fold' ? <AboveTheFoldGlyph size={15} /> : null}
                {subTabLabel(id)}
              </SurfaceTab>
            </React.Fragment>
          ))}
        </SurfaceTabs>
        {/* Keyed on the sub-tab so switching pool↔carousel replays the slide+fade. */}
        <SwitchFade key={effectiveSubTab}>
          {effectiveSubTab === 'above-the-fold' ? (
            <AboveTheFold
              status={aboveTheFold.status}
              atf={aboveTheFold.atf}
              onRetry={aboveTheFold.reload}
              cardsRegen={{
                cost: drawCost !== undefined ? drawCost * ATF_CARDS_DRAWS : undefined,
                run: cardsRun.run,
                queueing: cardsRun.queueing,
                onRegenerate: cardsRun.regenerate,
              }}
              cardRedraw={{
                cost: drawCost ?? cardRedraws.price,
                offers: cardRedraws.offers ?? {},
                pending: cardRedraws.pending
                  ? {
                      fieldName: cardRedraws.pending.fieldName,
                      status: cardRedraws.pending.status === 'ready' ? 'ready' : 'drawing',
                      candidateUrl: cardRedraws.pending.candidateUrl,
                      daysLeft: redrawDaysLeft(cardRedraws.pending.expiresAt, Date.now()),
                    }
                  : null,
                busy: cardRedraws.busy,
                onRedraw: cardRedraws.redraw,
                onApply: cardRedraws.apply,
                onDiscard: cardRedraws.discard,
              }}
            />
          ) : bannersState ? (
            bannersState
          ) : effectiveSubTab !== 'pool' ? (
            <CarouselCuration
              label={subTabLabel(effectiveSubTab)}
              poolItems={poolItems}
              // The hook shows the owner's saved draft, or the carousel's CURRENT live order when they
              // haven't curated it yet (baseline falls back to live), so it opens on the real deployed
              // order and a no-op publish changes nothing (manual curation wins the moment they edit).
              selectedIds={carouselSource.order}
              baselineIds={carouselSource.baseline}
              // The skeleton until the carousel's order has been read, an error with a retry if that read failed;
              // its empty copy only for a read that answered with no banners.
              loadStatus={carouselSource.status}
              onRetry={carouselSource.reload}
              onChange={carouselSource.setDraft}
              onPublish={() => handleCarouselPublish(subTabLabel(effectiveSubTab))}
              canRevert={carouselSource.canRevert}
              onRevert={() => handleCarouselRevert(subTabLabel(effectiveSubTab))}
              onEditBanner={(item) =>
                openEditBanner(pool?.placement ?? '', item, item.headline || item.internal_name || t('Banner'))
              }
              onPreview={(bannerId) => handlePreviewOnSite({ carousel: effectiveSubTab, bannerId })}
            />
          ) : (
            groups.map((group, groupIndex) => (
              <Section key={group.key || 'uncategorised'}>
                <SectionHead>
                  <SectionTitleRow>
                    <SectionName text={categoryLabel(group.tokens)} />
                    <Count>{group.banners.length}</Count>
                  </SectionTitleRow>
                </SectionHead>
                <Grid>
                  {group.banners.map(({ placement: itemPlacement, item }, index) => {
                    const label = item.headline || item.internal_name || t('Banner');
                    const first = groupIndex === 0 && index === 0;
                    return (
                      <CreativeCard
                        key={`${itemPlacement}:${item.banner_id}`}
                        enterIndex={index}
                        item={item}
                        placementLabel={label}
                        plain
                        busy={Boolean(activeByRoot[item.banner_id])}
                        busyLabel={busyLabelFor(item.banner_id)}
                        busyProgress={genProgressByRoot[item.banner_id]}
                        newLook={newLook.byBanner[item.banner_id]}
                        onEdit={() => openEditBanner(itemPlacement, item, label)}
                        ftuxCard={first ? FIRST_BANNER_TARGET : undefined}
                        ftuxEdit={first ? FIRST_BANNER_EDIT_TARGET : undefined}
                      />
                    );
                  })}
                </Grid>
              </Section>
            ))
          )}
        </SwitchFade>
      </>
    );
  };

  // The brand's logo family (real per-slot marks, not the chrome placements), offered in the
  // custom studio's "+" menu. `details.logo` is a single URL or a slot→{src,alt} map.
  const brandLogos = React.useMemo(() => {
    const raw = displayBrand?.casino_data?.details?.logo;
    if (typeof raw === 'string') return raw ? [raw] : [];
    if (!raw) return [];
    const urls = Object.entries(raw)
      .filter(([slot]) => !LOGO_CHROME_SLOTS.has(slot))
      .map(([, asset]) => asset?.src)
      .filter((u): u is string => Boolean(u));
    return Array.from(new Set(urls));
  }, [displayBrand]);

  // The brand's existing banner artwork, offered as pick-able backgrounds in the custom studio.
  const brandBanners = React.useMemo(() => {
    const urls = (creatives.family?.placements ?? [])
      .flatMap((p) => p.items)
      .map((i) => i.image_url)
      .filter((u): u is string => Boolean(u));
    return Array.from(new Set(urls)).slice(0, 24);
  }, [creatives.family]);

  // Promotions whose key art the video composer can animate (real banners only; the build-ahead
  // logo family has none). Recomputed each render (cheap, and the family reference is stable).
  const videoItems = isLogos ? [] : placements.flatMap((p) => p.items);

  const renderCustom = (): React.ReactElement => (
    <>
      {/* Keyed on the sub-tab so switching replays the slide+fade and each tab's library mounts fresh (an
          in-flight run is recovered from the ad's linked job, though a typed prompt/draft is not preserved). */}
      {videoUnpriced ? (
        <Callout
          tone="warning"
          title={t('Not available right now')}
          text="Video Ads can't open without their prices, which could not be read. Try again in a moment."
        />
      ) : (((urlVideoView && videoEnabled) || urlAdRoute) && !pricingLoaded) || !effectiveAssetTab ? (
        // A /videos, /videos-v2, /banners, /html5-banners or /mini-games deep link before the price table lands: its
        // sub-tab is not derivable yet (video needs beatCost; the ad tabs appear once pricing has loaded), so hold a
        // skeleton instead of flashing another tab. Same on the plain route while no tab is available yet (Image
        // Banners on, prices loading). A gate-off URL is answered by the route wrapper, never here.
        <Skeleton block height={196} radius={14} />
      ) : (
        <SwitchFade key={effectiveAssetTab}>
          {activeVideoPipeline ? (
            // $bleed zeroes the Section's first-of-type top margin in immersive so the full-bleed canvas
            // starts flush at the top-bar line (the composer height anchor assumes no margin above it).
            <Section $bleed={advancedImmersed}>
              <VideoLibrary
                pipeline={activeVideoPipeline}
                tenantId={tenantId}
                brandName={brandName}
                brandDomain={brandDomain}
                brandLogoUrl={brandLogoUrl}
                items={videoItems}
                itemsLoaded={!creatives.loading}
                beatCost={beatCost}
                view={videoView}
                immersed={advancedImmersed}
                onBuyCredits={openBuy}
                onNotify={show}
              />
            </Section>
          ) : activeAdTab === 'banner-kits' ? (
            <Section>
              <BannerKitLibrary
                tenantId={tenantId}
                brandName={brandName}
                brandDomain={brandDomain}
                brandLogoUrl={adBrandLogoUrl}
                brandLogos={adBrandLogos}
                items={videoItems}
                itemsLoaded={!creatives.loading}
                view={adView}
                onBuyCredits={openBuy}
                onNotify={show}
              />
            </Section>
          ) : activeAdTab === 'html5-banners' ? (
            <Section>
              <Html5BannerLibrary
                tenantId={tenantId}
                brandName={brandName}
                brandDomain={brandDomain}
                brandLogoUrl={adBrandLogoUrl}
                items={videoItems}
                itemsLoaded={!creatives.loading}
                view={adView}
                onBuyCredits={openBuy}
                onNotify={show}
              />
            </Section>
          ) : activeAdTab === 'landing-pages' ? (
            <Section>
              <LandingPageLibrary
                tenantId={tenantId}
                brandName={brandName}
                brandDomain={brandDomain}
                items={videoItems}
                itemsLoaded={!creatives.loading}
                view={adView}
                onBuyCredits={openBuy}
                onNotify={show}
              />
            </Section>
          ) : activeAdTab === 'playables' ? (
            <Section>
              <PlayableLibrary
                tenantId={tenantId}
                brandName={brandName}
                brandDomain={brandDomain}
                brandLogos={adGameLogos}
                items={videoItems}
                itemsLoaded={!creatives.loading}
                view={adView}
                onBuyCredits={openBuy}
                onNotify={show}
              />
            </Section>
          ) : (
            <CustomStudio
              brandName={brandName}
              brandLogoUrl={brandLogoUrl}
              tenantId={tenantId}
              onBuyCredits={() => setBuyOpen(true)}
              onNotify={show}
              brandBanners={brandBanners}
              brandLogos={brandLogos}
              onImmerseChange={setCustomImmersed}
            />
          )}
        </SwitchFade>
      )}
    </>
  );

  // The banner being edited, looked up LIVE from the family so the sheet's version strip reflects a
  // regeneration or restore the moment the family refetches (the `editing` snapshot alone is stale).
  const editingItem = editing?.bannerId
    ? placements.flatMap((p) => p.items).find((it) => it.banner_id === editing.bannerId)
    : undefined;
  // A regeneration in flight against the banner being edited (a publish is tracked separately) →
  // the sheet morphs to show a live "generating" tile. `activeByRoot` maps root banner id → kind.
  const editingBusyKind = editing?.bannerId ? activeByRoot[editing.bannerId] : undefined;
  // The edited banner's New look, its art read once it is ready. A regeneration in place redraws the live banner
  // itself, so there is no new look to preview and the sheet gets none.
  const editingBannerStatus = editing?.bannerId && !isLogos ? newLook.byBanner[editing.bannerId] : undefined;
  const editingNewLookStatus =
    editingBannerStatus && isNewLookPhaseStatus(editingBannerStatus) ? editingBannerStatus : undefined;
  const editingNewLookArt = useNewLookFormats(tenantId, editing?.bannerId, editingNewLookStatus, newLook.jobId);
  const editingGenerating = Boolean(editingBusyKind) && editingBusyKind !== 'publish';
  // Whether the banner's MOST RECENT run failed — so the editor can stop "awaiting" a result that a
  // failed run will never produce (a queued/running newer job supersedes it, so this is false again
  // once a retry starts). Only consulted by the sheet while it is actually awaiting a result.
  const editingRunFailed = React.useMemo(() => {
    const bannerId = editing?.bannerId;
    if (!bannerId) return false;
    let newest: (typeof jobsById)[string] | undefined;
    for (const j of Object.values(jobsById)) {
      if (j.root_banner_id !== bannerId) continue;
      if (!newest || (j.created_at ?? '') > (newest.created_at ?? '')) newest = j;
    }
    return newest?.status === 'failed';
  }, [jobsById, editing]);

  return (
    // Website Creatives and Game Lists share the Website section's frame and header, the area header or, with the
    // `website-hub` flag off, the tab row (with Logos, Art Style and Blog); a Marketing area has its own header, the
    // Marketing hub's back button (below).
    <WebsitePage styleText={styleText} className={className} header={section !== 'custom'}>
      {/* A Marketing area is reached from the Marketing hub, so it is headed by the hub's back button and its name
          (no tab row), hidden while an editor is immersed (it owns the screen). Here, not inside the fade below:
          BackHeader pins under the top bar only while its parent is the page column. */}
      {section === 'custom' && marketingArea && !customImmersed && !advancedImmersed ? (
        <MarketingAreaHeader title={assetTabLabel(marketingArea)} />
      ) : null}
      {/* No page title/subtitle: the breadcrumb already names the section, and credits are bought
          on demand (a generation that can't be afforded opens Buy-credits), not from a header CTA. */}
      {/* The History and Live-preview tabs are intentionally hidden (render code kept but unreachable —
          the live preview is reached via each carousel's "Preview on site", not a top-level tab). */}

      {/* Keyed on the section so moving between tabs replays the slide+fade. Games holds a control over the bottom
          navigation, so its fade leaves no stacking context behind (see SwitchFade). */}
      <SwitchFade key={section} $free={section === 'games'}>
        {section === 'custom' ? (
          renderCustom()
        ) : section === 'games' ? (
          <GameLists
            // Keyed on the brand: the studio never remounts pages on a brand switch, but every game-list
            // cache (contents, drafts, published, provider names) is keyed by data-source id, and the spk-*
            // ids are the same across brands — so without a remount brand B would show brand A's games and
            // publish A's order to B's instance. Remounting on the brand clears all of it. id first: it is
            // the publish target (`casinoInstanceId`), so it also remounts if one tenant ever exposed two
            // casino instances; tenant_id backs it up so two id-less brands can't collide on 'no-brand'.
            key={displayBrand?.id ?? displayBrand?.tenant_id ?? 'no-brand'}
            siteUrl={siteUrl}
            // Still resolving while the brand itself is on its way: no domain yet is not "no website".
            resolving={siteHostResolving || !brandSettled}
            casinoInstanceId={displayBrand?.id}
            onNotify={show}
          />
        ) : (
          renderBanners()
        )}
      </SwitchFade>

      {/* Big "Preview on site" modal: the embedded website with the draft order applied, the target
          banner highlighted, and the carousel's page shown — near-full-height for max real estate. Opens
          INSTANTLY on click; the preview URL (which needs an async guest-token scrape) resolves behind it,
          with LivePreview showing its loader until then. */}
      {previewTarget ? (
        <PreviewOverlay
          title="Preview"
          center={<DevicePreviewPill value={previewDevice} onChange={setPreviewDevice} />}
          onClose={() => {
            setPreviewTarget(null);
            setPreviewDevice('desktop');
            // Consume the optimistic trigger on close so reopening the preview doesn't replay the last
            // publish's swap (a bare iframe reveal would otherwise re-fire the reconcile for a stale seq).
            setOptimisticPublish(null);
          }}
        >
          <LivePreview
            siteUrl={siteUrl}
            onBannerClick={handleBannerClick}
            target={previewTarget}
            getPreviewSource={getPreviewSource}
            optimisticPublish={optimisticPublish}
            device={previewDevice}
            fill
          />
        </PreviewOverlay>
      ) : null}

      {editing ? (
        <EditCreativeSheet
          placement={editing.placement}
          placementLabel={editing.label}
          currentImageUrl={editingItem?.image_url ?? editing.baseImageUrl}
          versions={editingItem?.versions}
          publishedVersionId={editingItem?.published_version_id}
          generating={editingGenerating}
          generatingKind={editingBusyKind}
          generatingVersionId={editing.bannerId ? formatsVersionByRoot[editing.bannerId] : undefined}
          runFailed={editingRunFailed}
          runProgress={editing.bannerId ? genProgressByRoot[editing.bannerId] : undefined}
          formatCount={editingItem?.format_count}
          onPublish={
            editing.bannerId
              ? (versionId) =>
                  handlePublish(
                    editing.bannerId as string,
                    versionId,
                    editingItem?.versions?.find((v) => v.id === versionId),
                  )
              : undefined
          }
          onGenerateFormats={
            editing.bannerId ? (versionId) => handleGenerateFormats(editing.bannerId as string, versionId) : undefined
          }
          onDelete={editing.bannerId ? handleDeleteVersion : undefined}
          onRestore={editing.bannerId ? handleRestoreVersion : undefined}
          pending={creatives.pending}
          preview={creatives.preview}
          committing={creatives.committing}
          failure={creatives.failure}
          onGenerate={(input) =>
            handleGenerate(editing.placement, {
              bannerId: editing.bannerId,
              // A draw from the banner's definition is fresh: never fill in a base image for it.
              baseImageUrl: input.useDefinition ? undefined : editing.baseImageUrl,
              ...input,
            })
          }
          onPlaceOwn={(dataUri) => void handlePlaceOwn(editing.placement, dataUri)}
          onApply={() => void handleApply(editing.placement)}
          onDiscard={creatives.discard}
          onBuyCredits={() => openBuy()}
          onClose={() => setEditing(null)}
          allowAiModes={!isLogos}
          newLook={editingNewLookStatus ? { status: editingNewLookStatus, ...editingNewLookArt } : undefined}
          tenantId={tenantId}
          bannerId={editing.bannerId}
        />
      ) : null}

      {buyOpen ? (
        <BuyCreditsSheet
          suggestedCredits={buySuggested}
          onClose={() => setBuyOpen(false)}
          onSuccess={() => show(t('Credits added to your balance.'))}
        />
      ) : null}

      <Toast toast={toast} />
    </WebsitePage>
  );
};

export default Creatives;
